/**
 * Related Products Quick Add Handler
 * Handles quick add modal functionality for related product swatches
 * Replaces content in existing modal instead of opening new modal
 */

class RelatedProductsQuickAdd {
  constructor() {
    this.init();
  }

  init() {
    // Check if we're in a Shopify environment and have the required components
    if (!window.theme || !window.theme.routes) {
      console.warn('RelatedProductsQuickAdd: Shopify theme object not found');
      return;
    }
    
    console.log('RelatedProductsQuickAdd: Initialized');
    this.bindEvents();
  }

  bindEvents() {
    // Use event delegation to catch clicks on related product swatches
    document.addEventListener('click', (event) => {
      // Check if click is on a related product quick add swatch
      const swatchButton = event.target.closest('[data-related-product-quick-add]');
      
      if (swatchButton) {
        console.log('RelatedProductsQuickAdd: Swatch clicked', swatchButton);
        event.preventDefault();
        event.stopPropagation();
        
        // Get product handle from the button
        const productHandle = swatchButton.getAttribute('data-quick-add-modal-handle') || 
                             swatchButton.getAttribute('data-product-handle');
        
        console.log('RelatedProductsQuickAdd: Product handle:', productHandle);
        
        if (productHandle) {
          this.loadRelatedProduct(productHandle, swatchButton);
        } else {
          console.error('RelatedProductsQuickAdd: No product handle found');
        }
        
        return false;
      }
    }, true); // Use capture phase
  }

  /**
   * Load related product content
   * Replaces content in current modal or opens new modal if none exists
   */
  async loadRelatedProduct(productHandle, triggerElement) {
    try {
      console.log('RelatedProductsQuickAdd: Loading product:', productHandle);
      
      // Show loading state
      this.showLoadingState(triggerElement);
      
      // Check if we're already inside a modal
      const currentModal = triggerElement.closest('[data-quick-add-modal]');
      
      if (currentModal) {
        console.log('RelatedProductsQuickAdd: Found existing modal, replacing content');
        // We're in a modal, replace the content
        await this.replaceModalContent(currentModal, productHandle);
      } else {
        console.log('RelatedProductsQuickAdd: No existing modal found, creating new one');
        // No current modal, create new one (fallback)
        await this.createAndOpenModal(productHandle);
      }
      
    } catch (error) {
      console.error('RelatedProductsQuickAdd: Error loading product:', error);
      this.showErrorState(triggerElement);
      
      // Show error message in modal if we're in one
      const currentModal = triggerElement.closest('[data-quick-add-modal]');
      if (currentModal) {
        this.showModalError(currentModal, error.message, productHandle);
      }
    } finally {
      this.hideLoadingState(triggerElement);
    }
  }

  /**
   * Replace content in existing modal
   */
  async replaceModalContent(modal, productHandle) {
    console.log('RelatedProductsQuickAdd: Replacing modal content for:', productHandle);
    console.log('RelatedProductsQuickAdd: Modal element:', modal);
    
    // Fetch product content using the same API as QuickAddProduct
    const apiUrl = `${window.theme.routes.root}products/${productHandle}?section_id=api-product-upsell`;
    console.log('RelatedProductsQuickAdd: Fetching from:', apiUrl);
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      console.error('RelatedProductsQuickAdd: API request failed:', response.status, response.statusText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseText = await response.text();
    console.log('RelatedProductsQuickAdd: Response received, length:', responseText.length);
    console.log('RelatedProductsQuickAdd: Response preview:', responseText.substring(0, 200) + '...');
    
    // Find the content container in the current modal
    const contentContainer = modal.querySelector('[data-product-upsell-ajax]');
    if (!contentContainer) {
      console.error('RelatedProductsQuickAdd: Content container not found in modal');
      console.error('RelatedProductsQuickAdd: Available containers:', modal.querySelectorAll('[data-*]'));
      
      // Try alternative selectors
      const altContainer = modal.querySelector('.product-quick-add__inner') || 
                          modal.querySelector('.drawer__inner') ||
                          modal.querySelector('[data-scroll-lock-scrollable]');
      
      if (altContainer) {
        console.log('RelatedProductsQuickAdd: Using alternative container:', altContainer);
        this.replaceInContainer(altContainer, responseText, productHandle);
        return;
      }
      
      throw new Error('No suitable content container found in modal');
    }
    
    console.log('RelatedProductsQuickAdd: Found content container:', contentContainer);
    this.replaceInContainer(contentContainer, responseText, productHandle);
  }

  /**
   * Replace content in a specific container
   */
  replaceInContainer(container, responseText, productHandle) {
    // Add loading class to modal for smooth transition
    const modal = container.closest('[data-quick-add-modal]');
    if (modal) {
      modal.classList.add('loading-content');
    }
    
    // Replace the content with fade effect
    container.style.transition = 'opacity 0.15s ease-in-out';
    container.style.opacity = '0';
    
    setTimeout(() => {
      // Update the product ID attribute
      if (modal) {
        modal.setAttribute('data-product-id', productHandle);
      }
      
      // Replace content
      container.innerHTML = responseText;
      console.log('RelatedProductsQuickAdd: Content replaced in container');
      
      // Re-initialize any components in the new content
      this.initializeModalContent(container);
      
      // Fade content back in and ensure visibility
      container.style.opacity = '1';
      container.style.visibility = 'visible';
      container.style.display = 'block';
      
      // Force visibility on all child elements
      const allContent = container.querySelectorAll('*');
      allContent.forEach(el => {
        if (el.style.opacity === '0' || el.style.visibility === 'hidden') {
          el.style.opacity = '';
          el.style.visibility = '';
        }
      });
      
      if (modal) {
        modal.classList.remove('loading-content');
      }
      
      // Scroll to top of modal content
      const scrollableArea = modal?.querySelector('[data-scroll-lock-scrollable]') || container;
      if (scrollableArea && scrollableArea.scrollTop !== undefined) {
        scrollableArea.scrollTop = 0;
      }
      
      // Debug: Check if content is actually visible
      const computedStyle = window.getComputedStyle(container);
      console.log('RelatedProductsQuickAdd: Container computed style:', {
        opacity: computedStyle.opacity,
        visibility: computedStyle.visibility,
        display: computedStyle.display,
        height: computedStyle.height,
        overflow: computedStyle.overflow
      });
      
      // Debug: Check content structure in detail
      const apiContent = container.querySelector('[data-api-content]');
      if (apiContent) {
        console.log('RelatedProductsQuickAdd: API content found:', apiContent);
        console.log('RelatedProductsQuickAdd: API content children:', apiContent.children.length);
        
        // Check API content styles
        const apiContentStyle = window.getComputedStyle(apiContent);
        console.log('RelatedProductsQuickAdd: API content computed style:', {
          opacity: apiContentStyle.opacity,
          visibility: apiContentStyle.visibility,
          display: apiContentStyle.display,
          height: apiContentStyle.height,
          backgroundColor: apiContentStyle.backgroundColor,
          color: apiContentStyle.color
        });
        
        // Check if there are any images
        const images = apiContent.querySelectorAll('img');
        console.log('RelatedProductsQuickAdd: Images found:', images.length);
        
        // Check if there are any forms
        const forms = apiContent.querySelectorAll('form, product-form');
        console.log('RelatedProductsQuickAdd: Forms found:', forms.length);
        
        // Check if there's any text content
        console.log('RelatedProductsQuickAdd: API content text length:', apiContent.textContent.trim().length);
        console.log('RelatedProductsQuickAdd: API content HTML preview:', apiContent.innerHTML.substring(0, 500));
        
      } else {
        console.log('RelatedProductsQuickAdd: No API content found');
        console.log('RelatedProductsQuickAdd: Container HTML:', container.innerHTML.substring(0, 500));
      }
      
      console.log('RelatedProductsQuickAdd: Content replacement complete');
      
    }, 150); // Short delay for smooth transition
  }

  /**
   * Create and open new modal (fallback)
   */
  async createAndOpenModal(productHandle) {
    console.log('RelatedProductsQuickAdd: Creating new modal for:', productHandle);
    
    // Fetch product content using the same API as QuickAddProduct
    const response = await fetch(`${window.theme.routes.root}products/${productHandle}?section_id=api-product-upsell`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseText = await response.text();
    
    // Find the modal template
    const modalTemplate = document.querySelector('[data-quick-add-modal-template]');
    if (!modalTemplate) {
      throw new Error('Quick add modal template not found');
    }
    
    // Clone the template
    const modalClone = modalTemplate.content.cloneNode(true);
    const modal = modalClone.querySelector('[data-quick-add-modal]');
    
    if (!modal) {
      throw new Error('Modal element not found in template');
    }
    
    // Set product ID attribute for future reference
    modal.setAttribute('data-product-id', productHandle);
    
    // Insert the fetched content into the modal
    const contentContainer = modal.querySelector('[data-product-upsell-ajax]');
    if (contentContainer) {
      contentContainer.innerHTML = responseText;
    }
    
    // Add modal to DOM
    document.body.appendChild(modal);
    
    // Initialize modal functionality
    this.initializeModal(modal);
    
    // Open the modal
    this.openModal(modal);
  }

  /**
   * Initialize content within modal container
   */
  initializeModalContent(container) {
    console.log('RelatedProductsQuickAdd: Initializing modal content');
    
    // Initialize product form functionality
    this.initializeProductForm(container);
    
    // Initialize any other theme components that might be in the new content
    this.initializeThemeComponents(container);
  }

  /**
   * Initialize product form functionality within modal
   */
  initializeProductForm(container) {
    // Look for product form elements and initialize them
    const productForm = container.querySelector('product-form');
    if (productForm) {
      console.log('RelatedProductsQuickAdd: Found product form, initializing');
      if (typeof productForm.init === 'function') {
        productForm.init();
      }
    }
    
    // Initialize variant selectors
    const variantSelectors = container.querySelectorAll('[data-single-option-selector]');
    console.log('RelatedProductsQuickAdd: Found', variantSelectors.length, 'variant selectors');
    
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        // Trigger variant change events
        const event = new Event('variant:change', { bubbles: true });
        selector.dispatchEvent(event);
      });
    });
    
    // Initialize any new related product swatches in the loaded content
    const newRelatedSwatches = container.querySelectorAll('[data-related-product-quick-add]');
    if (newRelatedSwatches.length > 0) {
      console.log('RelatedProductsQuickAdd: Found', newRelatedSwatches.length, 'new related product swatches');
    }
  }

  /**
   * Initialize other theme components that might be in the new content
   */
  initializeThemeComponents(container) {
    // Initialize tooltips
    const tooltips = container.querySelectorAll('tooltip-component');
    tooltips.forEach(tooltip => {
      if (tooltip.init && typeof tooltip.init === 'function') {
        tooltip.init();
      }
    });
    
    // Trigger theme-specific events for component initialization
    container.dispatchEvent(new CustomEvent('theme:component:load', {
      bubbles: true,
      detail: { container }
    }));
  }

  /**
   * Initialize modal functionality (close buttons, form handling, etc.)
   */
  initializeModal(modal) {
    // Close button functionality
    const closeButtons = modal.querySelectorAll('[data-quick-add-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeModal(modal);
      });
    });
    
    // Close on backdrop click
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        this.closeModal(modal);
      }
    });
    
    // Close on escape key
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeModal(modal);
      }
    });
    
    // Initialize any product form components within the modal
    this.initializeProductForm(modal);
  }

  /**
   * Open modal with proper animations and events
   */
  openModal(modal) {
    if (!modal) return;
    
    // Trigger theme event to close other modals/drawers
    document.dispatchEvent(new CustomEvent('theme:quick-add:open', {bubbles: true}));
    
    // Show the modal
    modal.showModal();
    modal.removeAttribute('inert');
    
    // Focus management
    const closeButton = modal.querySelector('[data-quick-add-modal-close]');
    if (closeButton) {
      closeButton.focus();
    }
    
    // Add scroll lock
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close modal
   */
  closeModal(modal) {
    if (!modal) return;
    
    // Add closing class for animation
    modal.setAttribute('closing', '');
    
    // Remove scroll lock
    document.body.style.overflow = '';
    
    // Close after animation
    setTimeout(() => {
      modal.close();
      modal.setAttribute('inert', '');
      modal.removeAttribute('closing');
    }, 300); // Match CSS animation duration
  }

  /**
   * Loading state management
   */
  showLoadingState(element) {
    if (element) {
      element.style.opacity = '0.6';
      element.style.pointerEvents = 'none';
      element.classList.add('loading');
    }
  }

  hideLoadingState(element) {
    if (element) {
      element.style.opacity = '';
      element.style.pointerEvents = '';
      element.classList.remove('loading');
    }
  }

  showErrorState(element) {
    if (element) {
      element.style.border = '2px solid #e74c3c';
      setTimeout(() => {
        element.style.border = '';
      }, 3000);
    }
  }

  /**
   * Show error message in modal
   */
  showModalError(modal, errorMessage, productHandle) {
    const contentContainer = modal.querySelector('[data-product-upsell-ajax]') || 
                           modal.querySelector('.product-quick-add__inner') ||
                           modal.querySelector('.drawer__inner');
    
    if (contentContainer) {
      contentContainer.innerHTML = `
        <div style="padding: 2rem; text-align: center; color: #666;">
          <h3 style="margin-bottom: 1rem; color: #e74c3c;">Unable to Load Product</h3>
          <p style="margin-bottom: 1rem;">Sorry, we couldn't load the product "${productHandle}".</p>
          <p style="font-size: 0.9em; margin-bottom: 1.5rem;">${errorMessage}</p>
          <button onclick="this.closest('[data-quick-add-modal]').querySelector('[data-quick-add-modal-close]').click()" 
                  style="background: var(--COLOR-ACCENT, #000); color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </div>
      `;
      
      modal.classList.remove('loading-content');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RelatedProductsQuickAdd();
  });
} else {
  // DOM is already loaded
  new RelatedProductsQuickAdd();
}

// Export for potential use by other scripts
window.RelatedProductsQuickAdd = RelatedProductsQuickAdd;